import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

import type { IProjectAsset } from "../../interfaces";

export default function useProjectAsset() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useList(projectId?: string) {
        return useQuery({
            queryKey: ["project-assets", projectId],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/project-assets/list", {
                    params: { ProjectId: projectId },
                });
                return parseSuccessResponseData(response) as IProjectAsset[];
            },
            enabled: !!projectId,
        });
    }

    function useUpload() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: async (parameters: {
                ProjectId: string;
                File: File;
                Note?: string;
                onProgress?: (progress: { loaded: number; total: number; percent: number }) => void;
            }) => {
                const formData = new FormData();
                formData.append("ProjectId", parameters.ProjectId);
                formData.append("File", parameters.File);
                if (parameters.Note) {
                    formData.append("Note", parameters.Note);
                }
                const response = await axiosPrivate.post("/v1/project-assets/upload", formData, {
                    onUploadProgress: (event) => {
                        // `event.total` covers the whole encoded body, so it can exceed
                        // the file size slightly; clamp to keep the percentage sane.
                        const total = event.total || parameters.File.size;
                        if (!total || !parameters.onProgress) return;
                        const loaded = Math.min(event.loaded, total);
                        parameters.onProgress({
                            loaded,
                            total,
                            percent: Math.round((loaded / total) * 100),
                        });
                    },
                });
                return parseSuccessResponseData(response) as IProjectAsset;
            },
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ["project-assets", variables.ProjectId] });
                queryClient.invalidateQueries({ queryKey: ["project-timeline", variables.ProjectId] });
            },
        });
    }

    function useDelete() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (parameters: { Id: string; ProjectId: string }) =>
                axiosPrivate
                    .delete(`/v1/project-assets/delete/${parameters.Id}`)
                    .then(parseSuccessResponseData),
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ["project-assets", variables.ProjectId] });
                queryClient.invalidateQueries({ queryKey: ["project-timeline", variables.ProjectId] });
            },
        });
    }

    return { useList, useUpload, useDelete };
}
